import { useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, X } from "lucide-react";
import PasswordInput from "@/components/ui/PasswordInput";

/**
 * Small inline "Change password" card for the user Dashboard.
 * Uses POST /api/auth/change-password.
 */
export default function ChangePasswordCard({ hasPassword = true }) {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: hasPassword ? cur : undefined, new_password: next });
      toast.success("Password updated.");
      setOpen(false); setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="wofy-card mt-6" data-testid="change-password-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4" style={{ color: "var(--wofy-text-soft)" }} />
          <div className="overline">SECURITY</div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="change-password-open" className="wofy-btn-ghost text-xs">
            Change password
          </button>
        )}
        {open && (
          <button onClick={() => setOpen(false)} className="wofy-btn-ghost text-xs" aria-label="Close">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          {hasPassword && (
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider">Current password</span>
              <PasswordInput data-testid="cp-current" required autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
              <Link
                to="/forgot-password"
                data-testid="cp-forgot-current"
                className="mt-1 inline-block text-xs underline"
                style={{ color: "var(--wofy-primary)" }}
              >
                Forgot current password? Reset it by email instead.
              </Link>
            </label>
          )}
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">New password (min 8)</span>
            <PasswordInput data-testid="cp-new" required minLength={8} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">Confirm new password</span>
            <PasswordInput data-testid="cp-confirm" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <button data-testid="cp-submit" disabled={busy} className="wofy-btn-primary text-sm disabled:opacity-60">
            {busy ? "Saving…" : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
}
